import {FC} from 'react'

import cn from 'classnames/bind'
import {observer} from 'mobx-react-lite'

import InputDate from 'components/elements/fields/InputDate'
import {Col, Row} from 'components/elements/grid'
import Tooltip from 'components/elements/Tooltip'
import {usePayStubStore} from 'src/utils/contexts/RootStore.context'
import useUpdateEffect from 'src/utils/hooks/useUpdateEffect'

import s from './FieldsPayStubEarningsStatement.module.scss'
import ItemDeductionsBlock from './ItemDeductionsBlock'
import ItemEarningsBlock from './ItemEarningsBlock'
const cx = cn.bind(s)

interface IPayStubItemProps {
	index: number
}

const PayStubItem: FC<IPayStubItemProps> = observer(({index}) => {
	const {items, setPayDate, setPayPeriodTo, setPayPeriodFrom, unValidateCalculation} =
		usePayStubStore()
	const item = items[index]

	useUpdateEffect(() => {
		unValidateCalculation()

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [item.grossPayTotal])

	return (
		<div className={cx('stub__item')} data-testid="stub">
			<div className={cx('stub__title')}>
				<h3>paystub-{index + 1}</h3>
			</div>

			<Row>
				<Col className={cx('stub__col')}>
					<div className={cx('stub__wrapper')}>
						<div className={cx('pay-period')}>
							<InputDate
								className={cx('pay-period__input')}
								labelClassName={cx('pay-period__label-wrapper')}
								labelAs="div"
								label={
									<span className={cx('pay-period__label')}>
										<span className={cx('pay-period__text')}>Pay period</span>
										<Tooltip
											className={cx('pay-period__tooltip')}
											text={
												index === 0 ? (
													<>
														This will be the latest pay period the user has worked. If the user
														selects more than 1 pay stub, the pay stubs will automatically back
														track from the latest pay stub.
													</>
												) : (
													<>This is a back dated from the paystub before this one.</>
												)
											}
										/>
									</span>
								}
								calendarIcon={null}
								clearIcon={null}
								format="MM-dd-y"
								variant="editable"
								// start of the year 4 years ago
								minDate={new Date(new Date().setFullYear(new Date().getFullYear() - 4, 0, 1))}
								// end of the next year
								maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 1, 11, 31))}
								minDetail="decade"
								onChange={(v: Date) => setPayPeriodFrom(v, index)}
								value={item.payPeriod.from}
							/>
							<span className={cx('pay-period__dash')}></span>
							<InputDate
								className={cx('pay-period__input')}
								calendarIcon={null}
								clearIcon={null}
								format="MM-dd-y"
								variant="editable"
								// start of the year 4 years ago
								minDate={new Date(new Date().setFullYear(new Date().getFullYear() - 4, 0, 1))}
								// end of the next year
								maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 1, 11, 31))}
								minDetail="decade"
								onChange={(v: Date) => {
									setPayPeriodTo(v, index)
								}}
								value={item.payPeriod.to}
							/>
						</div>
						<ItemEarningsBlock {...{index}} />
					</div>
				</Col>
				<Col className={cx('stub__col')}>
					<div className={cx('stub__wrapper')}>
						<div className={cx('pay-date')}>
							<InputDate
								className={cx('pay-date__input')}
								labelClassName={cx('pay-date__label-wrapper')}
								labelAs="div"
								// start of the year 3 years ago
								minDate={new Date(new Date().setFullYear(new Date().getFullYear() - 3, 0, 1))}
								// end of the current year
								maxDate={new Date(new Date().setMonth(11, 31))}
								minDetail="decade"
								label={
									<span className={cx('pay-date__label')}>
										Pay date
										<Tooltip
											className={cx('pay-date__tooltip')}
											text={
												<>
													<span>The Pay Date is the date you get paid.</span>
													<br />
													<span>
														If you get paid after the pay period, the pay dates for all paystubs
														will be the same amount of days in-between the Pay Date and the last day
														of the Pay Period.
													</span>
													<br />
													<span>
														If you get paid before your pay period, all paystubs will have the same
														amount of days in-between the Pay Date and the last day of the Pay
														Period
													</span>
												</>
											}
										/>
									</span>
								}
								calendarIcon={null}
								clearIcon={null}
								format="MM-dd-y"
								variant="editable"
								onChange={(v: Date) => {
									if (v === null) {
										setPayDate(new Date(item.payDate.setDate(1)), index)
									} else {
										setPayDate(v, index)
									}
								}}
								value={item.payDate}
							/>
						</div>
						<ItemDeductionsBlock {...{index}} />
					</div>
				</Col>
			</Row>
		</div>
	)
})

PayStubItem.displayName = 'PayStubItem'

export default PayStubItem
